/**
 * The popup channel: a stack of dismissible toasts fed by the alert runtime's event bus.
 *
 * Mounted once at the app root so an alert raised by any feed is visible wherever the user happens
 * to be — the alternative, rendering per view, would silently drop alerts from a tab you had left.
 *
 * A toast says what fired, which contract, and why, in the record's own numbers. When the record
 * identifies a contract the whole toast opens its history, so an alert is a way into the data
 * rather than a dead end.
 */

import React, { useEffect, useState } from 'react';
import './AlertRulesPanel.css';
import { AlertEvent, onAlert } from '../../utils/alertRuntime';
import { useHistoricalModal } from '../../contexts/HistoricalModalContext';
import { isDesignSystemOn } from '../../theme/mmecDsPreview';
import './AlertToasts.ds.css';

const VISIBLE_MS = 20000;
const MAX_VISIBLE = 4;

const AlertToasts: React.FC = () => {
  const [events, setEvents] = useState<AlertEvent[]>([]);
  const { openHistorical } = useHistoricalModal();

  useEffect(
    () =>
      onAlert((event) => {
        setEvents((prev) => [event, ...prev].slice(0, MAX_VISIBLE));
        window.setTimeout(() => {
          setEvents((prev) => prev.filter((e) => e.id !== event.id));
        }, VISIBLE_MS);
      }),
    [],
  );

  if (!events.length) return null;

  const open = (e: AlertEvent) => {
    if (!e.historical) return;
    openHistorical({ ...e.historical, source: 'volume' });
  };

  return (
    <div className="mmecAlertStack" role="status" aria-live="polite">
      {events.map((e) => (
        <div
          className={`mmecAlertToast${e.historical ? ' clickable' : ''}`}
          key={e.id}
          role={e.historical ? 'button' : undefined}
          tabIndex={e.historical ? 0 : undefined}
          title={e.historical ? 'Open contract history' : undefined}
          style={isDesignSystemOn() ? undefined : { boxShadow: 'var(--lift-3)', borderRadius: 'var(--radius)' }}
          onClick={() => open(e)}
          onKeyDown={(ev) => {
            if (e.historical && (ev.key === 'Enter' || ev.key === ' ')) {
              ev.preventDefault();
              open(e);
            }
          }}
        >
          <div className="body">
            <div className="rule">
              {e.ruleName}
              {e.sourceLabel ? <span className="src"> · {e.sourceLabel}</span> : null}
            </div>
            <div className="summary">{e.summary}</div>
            {e.details?.length ? (
              <ul className="why">
                {e.details.slice(0, 4).map((d) => (
                  <li key={d}>{d}</li>
                ))}
              </ul>
            ) : null}
            <div className="meta">
              {new Date(e.at).toLocaleTimeString()}
              {e.historical ? ' · click for history' : ''}
            </div>
          </div>
          <button
            type="button"
            className="close"
            aria-label="dismiss"
            onClick={(ev) => {
              ev.stopPropagation();
              setEvents((prev) => prev.filter((x) => x.id !== e.id));
            }}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
};

export default AlertToasts;
